import { Badge } from "@/components";
import { PermissionGate } from "@/components/common";
import { formatCurrency } from "@/lib/utils";
import { Product } from "@/types";
import { Switch } from "@radix-ui/react-switch";
import { Coffee } from "lucide-react";
import { Link } from "react-router-dom";
import { ProductRowMenu } from "./ProductRowMenu";

interface ListRowProps {
  products: Product[];
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onToggle: (id: string) => void;
  onView: (id: string) => void;
}

export function ProductsCardList({
  products,
  onEdit,
  onDelete,
  onToggle,
  onView,
}: ListRowProps) {
  return (
    <div className="space-y-3 lg:hidden">
      {products.map((product) => {
        const defaultVariant =
          product.variants?.find((v) => v.isDefault) || product.variants?.[0];

        return (
          <div key={product.id} className="rounded-lg border p-4">
            <div className="flex items-start gap-3">
              {product.imageUrl ? (
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  className="h-12 w-12 shrink-0 rounded-md object-cover"
                />
              ) : (
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-muted">
                  <Coffee className="h-5 w-5 text-muted-foreground" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Link
                      to={`/products/${product.id}`}
                      className="block font-medium underline-offset-2 hover:text-primary hover:underline"
                    >
                      {product.name}
                    </Link>
                    {product.description && (
                      <p className="line-clamp-1 text-xs text-muted-foreground">
                        {product.description}
                      </p>
                    )}
                  </div>
                  <ProductRowMenu
                    product={product}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onView={onView}
                  />
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                  {product.category && (
                    <Badge variant="outline">{product.category.name}</Badge>
                  )}
                  {defaultVariant && (
                    <span className="font-medium">
                      {formatCurrency(defaultVariant.price)}
                    </span>
                  )}
                  <span className="text-muted-foreground">
                    {product.variants?.length || 0} variant(s)
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span className="font-mono text-xs text-muted-foreground">
                {product.sku}
              </span>
              <PermissionGate
                permission="products.update"
                fallback={
                  <Badge variant={product.isActive ? "outline" : "secondary"}>
                    {product.isActive ? "Active" : "Inactive"}
                  </Badge>
                }
              >
                <Switch
                  checked={product.isActive}
                  onCheckedChange={() => onToggle(product.id)}
                />
              </PermissionGate>
            </div>
          </div>
        );
      })}
    </div>
  );
}
